import React from "react";
import { Node, useReactFlow } from "reactflow";
import { faCheck, faComments, faLongArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { faClipboard } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CopyToClipboard from "react-copy-to-clipboard";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coldarkDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ExternalLink } from "lucide-react";
import { Select } from "../../form/select";
import { operationType } from "../../../services/types";
import { consoleURL, webURL } from "../../../services/API";
import { Main } from "./style";
import { createPortal } from "react-dom";
import { PopupOverlay } from "../popupOverlay";

type GuideTab = 'html' | 'wordpress' | 'shopify' | 'react' | 'nextjs';
type EmbedTab = 'icon' | 'page';

const guides: { id: GuideTab, name: string }[] = [
   { id: 'html', name: 'HTML' },
   { id: 'wordpress', name: 'WordPress' },
   { id: 'shopify', name: 'Shopify' },
   { id: 'react', name: 'React' },
   { id: 'nextjs', name: 'Next.js' },
];

export function WebChatProject({ project, handleValuesFeatures }: { project?: projectType | null, handleValuesFeatures: (values: any) => void }) {
   const proj = project ?? null;
   const { getNodes } = useReactFlow();
   const nodes: Node<operationType>[] = getNodes();
   const [inputId, setInputId] = React.useState('');
   const [outputId, setOutputId] = React.useState('');
   const [copied, setCopied] = React.useState<'url' | 'embed' | null>(null);
   const [guideTab, setGuideTab] = React.useState<GuideTab>('html');
   const [embedTab, setEmbedTab] = React.useState<EmbedTab>('icon');

   const inputNodes = React.useMemo(() => nodes.filter(node => node.data.action === 'input'), [nodes]);
   const outputNodes = React.useMemo(() => nodes.filter(node => node.data.action === 'output'), [nodes]);
   // Best-effort: if the workflow has an AI Agent node, the web chat can target it directly
   // and skip the input/output picker entirely — same shortcut the old floating launcher used.
   const aiAgentNode = React.useMemo(() => nodes.find(node => node.data.action === 'aiAgent'), [nodes]);
   const useAgentMode = !!aiAgentNode;

   // Default to the only candidate when there's a single input/output in the workflow.
   React.useEffect(() => {
      if (!inputId && inputNodes.length === 1) setInputId(inputNodes[0].id);
      if (!outputId && outputNodes.length === 1) setOutputId(outputNodes[0].id);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [inputNodes.length, outputNodes.length]);

   const inputNode = nodes.find(node => node.id === inputId);
   const outputNode = nodes.find(node => node.id === outputId);
   const nodeIndex = aiAgentNode?.data.index as string | undefined;

   const isReady = useAgentMode
      ? !!(proj?._id && proj?.apiKey && nodeIndex)
      : !!(proj?._id && proj?.apiKey && inputNode && outputNode);

   const chatUrl = !isReady ? '' : useAgentMode
      ? `${consoleURL}/webchat/${proj!._id}?key=${encodeURIComponent(proj!.apiKey)}&node=${encodeURIComponent(nodeIndex as string)}`
      : `${consoleURL}/webchat/${proj!._id}?key=${encodeURIComponent(proj!.apiKey)}&input=${encodeURIComponent(inputNode!.data.index as string)}&output=${encodeURIComponent(outputNode!.data.index as string)}`;

   const embedCode = `<iframe\n  src="${chatUrl}"\n  width="380"\n  height="600"\n  style="border:none;border-radius:16px;box-shadow:0 4px 24px rgba(0,0,0,.12)"\n  title="Web Chat"\n></iframe>`;
   const reactSnippet = `<iframe\n  src="${chatUrl}"\n  style={{ width: 380, height: 600, border: 'none', borderRadius: 16 }}\n  title="Web Chat"\n/>`;
   const widgetScriptCode = !isReady ? '' : `<script\n  src="${consoleURL}/widget.js"\n  data-project-id="${proj!._id}"\n  data-api-key="${proj!.apiKey}"\n  data-backend-url="${webURL}"${
      useAgentMode
         ? `\n  data-node="${nodeIndex}"`
         : `\n  data-input="${inputNode!.data.index}"\n  data-output="${outputNode!.data.index}"`
   }\n  data-position="bottom-right"\n  async\n></script>`;
   const activeEmbedCode = embedTab === 'icon' ? widgetScriptCode : embedCode;

   // "Page" previews the hosted chat itself (that's exactly what the iframe embeds).
   // "Floating Icon" has no standalone URL to open — it only exists once the script tag
   // runs on a page — so build a throwaway blank page that loads the real widget script
   // and open that instead, giving a true preview of the corner bubble + popup.
   const handlePreviewEmbed = () => {
      if (embedTab === 'page') {
         window.open(chatUrl, '_blank');
         return;
      }
      const html = `<!doctype html><html><head><meta charset="utf-8" /><title>Floating Icon Preview</title></head>` +
         `<body style="margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;` +
         `font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#94a3b8;font-size:14px;">` +
         `Your website content goes here — the chat icon appears in the bottom-right corner.` +
         `${widgetScriptCode}</body></html>`;
      const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
   };

   const guideContent: Record<GuideTab, { code: string, lang: string, text: string }> = {
      html: { code: embedCode, lang: 'html', text: 'Paste this snippet anywhere inside the <body> of your HTML page, right before the closing </body> tag.' },
      wordpress: { code: embedCode, lang: 'html', text: 'Appearance → Theme File Editor → footer.php, paste this right before </body>. Or add a "Custom HTML" block to any page/post if you don’t want to edit theme files.' },
      shopify: { code: embedCode, lang: 'html', text: 'Online Store → Themes → Edit code → theme.liquid, paste this right before </body>, then Save.' },
      react: { code: reactSnippet, lang: 'jsx', text: "Paste this JSX inside the return(...) of any React component, e.g. directly in your page/component body." },
      nextjs: { code: reactSnippet, lang: 'jsx', text: 'Paste this JSX inside a page (app/page.tsx) or shared layout (app/layout.tsx) component’s returned markup.' },
   };

   const noIONodes = !useAgentMode && (!inputNodes.length || !outputNodes.length);

   return createPortal(
      <PopupOverlay onClick={ _=> handleValuesFeatures({ isWebChat: false }) }>
         <Main onClick={ e => e.stopPropagation() }>
            <div className="close" onClick={ _=> handleValuesFeatures({ isExport: true, isWebChat: false }) }>
               <FontAwesomeIcon icon={ faLongArrowLeft } />
            </div>
            <div className="title">
               <span className="icon">
                  <FontAwesomeIcon icon={ faComments } />
               </span>
               <div>
                  <h3>Web Chat</h3>
                  <p>Let visitors chat with this workflow on any website, anywhere.</p>
               </div>
            </div>

            { noIONodes ? (
               <div className="empty">
                  { !inputNodes.length && !outputNodes.length
                     ? "This workflow has no Input or Output blocks yet. Drag both in from the node panel (Process → I/O functions) and connect them to enable Web Chat."
                     : !inputNodes.length
                        ? "This workflow has no Input block yet. Drag one in from the node panel (Process → I/O functions) so visitors have something to type into."
                        : "This workflow has no Output block yet. Drag one in from the node panel (Process → I/O functions) to capture the reply." }
               </div>
            ) : (<>
               { !useAgentMode && (
                  <div className="picker">
                     <Select
                        title="Input"
                        emptyMsg="Choose the input block"
                        data={ inputNodes.map(node => ({ id: node.id, name: String(node.data.index || node.id), isActive: node.id === inputId })) }
                        handleSelectItem={ (id) => setInputId(id) }
                     />
                     <Select
                        title="Output"
                        emptyMsg="Choose the output block"
                        data={ outputNodes.map(node => ({ id: node.id, name: String(node.data.index || node.id), isActive: node.id === outputId })) }
                        handleSelectItem={ (id) => setOutputId(id) }
                     />
                  </div>
               )}

               { !isReady ? (
                  <div className="empty">
                     { useAgentMode ? "Save the project first — the export link and API key aren't ready yet." : "Pick an input and an output block to generate your chat link." }
                  </div>
               ) : (<>
               <div className="field">
                  <label>Chat URL</label>
                  <div className="row">
                     <input readOnly value={ chatUrl } onClick={ e => (e.target as HTMLInputElement).select() } />
                     <button type="button" title="Preview chat in a new tab" onClick={ _=> window.open(chatUrl, '_blank') }>
                        <ExternalLink size={ 16 } />
                     </button>
                     <CopyToClipboard text={ chatUrl } onCopy={ _=> { setCopied('url'); setTimeout(() => setCopied(null), 1200); } }>
                        <button type="button" title="Copy chat URL">
                           <FontAwesomeIcon icon={ copied === 'url' ? faCheck : faClipboard } />
                        </button>
                     </CopyToClipboard>
                  </div>
               </div>

               <div className="field">
                  <label>Embed on your website</label>
                  <div className="guideTabs">
                     <button type="button" className={ embedTab === 'icon' ? 'active' : '' } onClick={ _=> setEmbedTab('icon') }>Floating Icon</button>
                     <button type="button" className={ embedTab === 'page' ? 'active' : '' } onClick={ _=> setEmbedTab('page') }>Embedded Box</button>
                  </div>
                  <p className="guideText">
                     { embedTab === 'icon'
                        ? "Adds a small floating chat icon to the corner of the site. Visitors click it to open the chat in a popup — one script tag, works on any website."
                        : "Embeds a fixed-size chat box directly in the page layout — always visible, no floating icon." }
                  </p>
                  <div className="code">
                     <div className="codeTitle">
                        <span>html</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                           <div
                              className="copy"
                              title={ `Preview ${ embedTab === 'icon' ? 'Floating Icon' : 'Embedded Box' } in a new tab` }
                              onClick={ handlePreviewEmbed }
                           >
                              <ExternalLink size={ 13 } />
                           </div>
                           <CopyToClipboard text={ activeEmbedCode } onCopy={ _=> { setCopied('embed'); setTimeout(() => setCopied(null), 1200); } }>
                              <div className="copy">
                                 <FontAwesomeIcon icon={ copied === 'embed' ? faCheck : faClipboard } />
                                 <span>{ copied === 'embed' ? 'Copied' : 'Copy' }</span>
                              </div>
                           </CopyToClipboard>
                        </div>
                     </div>
                     <SyntaxHighlighter language="html" style={ coldarkDark }>{ activeEmbedCode }</SyntaxHighlighter>
                  </div>
               </div>

               { embedTab === 'page' && (
               <div className="field">
                  <label>Integration Guide</label>
                  <div className="guideTabs">
                     { guides.map(tab => (
                        <button type="button" key={ tab.id } className={ guideTab === tab.id ? 'active' : '' } onClick={ _=> setGuideTab(tab.id) }>
                           { tab.name }
                        </button>
                     ))}
                  </div>
                  <p className="guideText">{ guideContent[guideTab].text }</p>
                  <div className="code">
                     <div className="codeTitle"><span>{ guideContent[guideTab].lang }</span></div>
                     <SyntaxHighlighter language={ guideContent[guideTab].lang } style={ coldarkDark }>{ guideContent[guideTab].code }</SyntaxHighlighter>
                  </div>
                  <p className="guideText">Any other platform that lets you paste custom HTML/iframes (Wix, Squarespace, Webflow, Notion, etc.) works the same way — just paste the HTML snippet above into its embed/custom-code block.</p>
               </div>
               )}
               </>) }
            </>) }
         </Main>
      </PopupOverlay>,
      document.body
   );
}
